import React from 'react';
import { Activity, Timer, TrendingUp, TrendingDown, Mountain, Dumbbell } from 'lucide-react';

const Header = ({ activities }) => {
  // Calcular rangos de tiempo basados en la fecha actual
  const now = new Date();
  
  // Últimos 7 días (de hoy a hace 7 días)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(now.getDate() - 7);
  
  // Semana anterior (de hace 7 días a hace 14 días)
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(now.getDate() - 14);

  // Filtrar actividades correspondientes a los últimos 7 días
  const recentActivities = activities.filter(a => {
    const actDate = new Date(a.startDate || a.start_date);
    return actDate >= sevenDaysAgo;
  });

  // Filtrar actividades correspondientes a la semana anterior
  const previousActivities = activities.filter(a => {
    const actDate = new Date(a.startDate || a.start_date);
    return actDate >= fourteenDaysAgo && actDate < sevenDaysAgo;
  });

  // Volumen total (últimos 7 días)
  const currentWeekDuration = recentActivities.reduce((acc, curr) => acc + curr.moving_time, 0);
  
  // Volumen total (semana anterior)
  const lastWeekDuration = previousActivities.reduce((acc, curr) => acc + curr.moving_time, 0);
  
  // Calcular porcentaje de cambio entre semanas
  let percentageChange = 0;
  if (lastWeekDuration > 0) {
    percentageChange = (((currentWeekDuration - lastWeekDuration) / lastWeekDuration) * 100).toFixed(1);
  } else if (currentWeekDuration > 0) {
    percentageChange = 100.0;
  }
  const isPositive = percentageChange >= 0;

  const currentHours = (currentWeekDuration / 3600).toFixed(1);
  const lastHours = (lastWeekDuration / 3600).toFixed(1);

  // Tiempo de resistencia (Endurance) últimos 7 días
  const enduranceTime = recentActivities
    .filter(a => a.category === 'Endurance')
    .reduce((acc, curr) => acc + curr.moving_time, 0) / 3600;

  // Tiempo de gimnasio/indoor últimos 7 días
  const indoorTime = recentActivities
    .filter(a => a.category === 'Indoor/Gym')
    .reduce((acc, curr) => acc + curr.moving_time, 0) / 3600;

  // Desnivel acumulado en los últimos 7 días
  const totalElevation = Math.round(
    recentActivities.reduce((acc, curr) => acc + (curr.total_elevation_gain || 0), 0)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
      {/* TÍTULO DE LA SECCIÓN */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <h2 className="text-xl" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Activity size={20} style={{ color: 'var(--health-cyan)' }} /> Volumen y Carga Semanal
        </h2>
        <span className="text-muted text-sm">(Últimos 7 días)</span>
      </div>

      <div className="metric-cards-grid">
        {/* Tarjeta 1: Volumen Total */}
        <div style={{
          background: `radial-gradient(ellipse at top left, rgba(249,115,22,0.18) 0%, rgba(249,115,22,0.04) 60%, transparent 100%), rgba(10,15,25,0.7)`,
          border: `1px solid rgba(249,115,22,0.3)`,
          borderRadius: '14px', padding: '1rem 1.1rem',
          display: 'flex', flexDirection: 'column', gap: '6px',
          boxShadow: `0 4px 20px rgba(249,115,22,0.1), inset 0 1px 0 rgba(249,115,22,0.15)`,
          position: 'relative'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Volumen Total</span>
            <Timer size={14} style={{ color: '#f97316', opacity: 0.8 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', margin: '4px 0 2px' }}>
            <span style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}>{currentHours}</span>
            <span style={{ fontSize: '1rem', color: '#f97316', fontWeight: 600 }}>hrs</span>
          </div>
          <div className={isPositive ? 'trend-positive text-sm' : 'trend-down text-sm'} style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>{isPositive ? '+' : ''}{percentageChange}% vs sem. anterior ({lastHours}h)</span>
          </div>
        </div>

        {/* Tarjeta 2: Resistencia */}
        <div style={{
          background: `radial-gradient(ellipse at top left, rgba(56,189,248,0.18) 0%, rgba(56,189,248,0.04) 60%, transparent 100%), rgba(10,15,25,0.7)`,
          border: `1px solid rgba(56,189,248,0.3)`,
          borderRadius: '14px', padding: '1rem 1.1rem',
          display: 'flex', flexDirection: 'column', gap: '6px',
          boxShadow: `0 4px 20px rgba(56,189,248,0.1), inset 0 1px 0 rgba(56,189,248,0.15)`,
          position: 'relative'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Resistencia</span>
            <Activity size={14} style={{ color: '#38bdf8', opacity: 0.8 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', margin: '4px 0 2px' }}>
            <span style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}>{enduranceTime.toFixed(1)}</span>
            <span style={{ fontSize: '1rem', color: '#38bdf8', fontWeight: 600 }}>hrs</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
            Carrera, Ciclismo, Caminata
          </div>
        </div>

        {/* Tarjeta 3: Desnivel Acumulado */}
        <div style={{
          background: `radial-gradient(ellipse at top left, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.04) 60%, transparent 100%), rgba(10,15,25,0.7)`,
          border: `1px solid rgba(16,185,129,0.3)`,
          borderRadius: '14px', padding: '1rem 1.1rem',
          display: 'flex', flexDirection: 'column', gap: '6px',
          boxShadow: `0 4px 20px rgba(16,185,129,0.1), inset 0 1px 0 rgba(16,185,129,0.15)`,
          position: 'relative'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Desnivel</span>
            <Mountain size={14} style={{ color: '#10b981', opacity: 0.8 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', margin: '4px 0 2px' }}>
            <span style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}>{totalElevation.toLocaleString('es-ES')}</span>
            <span style={{ fontSize: '1rem', color: '#10b981', fontWeight: 600 }}>m</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
            Ascenso total acumulado
          </div>
        </div>

        {/* Tarjeta 4: Indoor / Gym */}
        <div style={{
          background: `radial-gradient(ellipse at top left, rgba(139,92,246,0.18) 0%, rgba(139,92,246,0.04) 60%, transparent 100%), rgba(10,15,25,0.7)`,
          border: `1px solid rgba(139,92,246,0.3)`,
          borderRadius: '14px', padding: '1rem 1.1rem',
          display: 'flex', flexDirection: 'column', gap: '6px',
          boxShadow: `0 4px 20px rgba(139,92,246,0.1), inset 0 1px 0 rgba(139,92,246,0.15)`,
          position: 'relative'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Indoor / Gym</span>
            <Dumbbell size={14} style={{ color: '#8b5cf6', opacity: 0.8 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', margin: '4px 0 2px' }}>
            <span style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}>{indoorTime.toFixed(1)}</span>
            <span style={{ fontSize: '1rem', color: '#8b5cf6', fontWeight: 600 }}>hrs</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
            Pesas, Cicloindoor, Estiramientos
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
